import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getProfessionals } from "../services/get-professionals";
import { Professional } from "@/pages/professionals/types/professional";

// The list endpoint returns the supervisor / lead rosters as projections, not
// full rows: type them as such so nothing reads a field that isn't there.
export type SupervisorOption = Pick<Professional, "id" | "name" | "status">;
export type LeadOption = Pick<Professional, "id" | "name">;

interface PaginatedProfessionals {
  professionals: Array<Professional>;
  lastPage: number;
  nextPage: number | null;
  currentPage: number;
  total: number;
  supervisors: Array<SupervisorOption>;
  leads: Array<LeadOption>;
}

export default function useProfessionals(
  page: number = 0,
  search?: string,
  status?: string,
  job_commitment?: string,
  qualification?: string[],
  type?: string,
  is_supervisor?: string,
  is_lead?: string,
  supervisor_id?: string
) {
  const { data, isLoading, error, isFetching, isSuccess } =
    useQuery<PaginatedProfessionals>({
      queryKey: [
        "professionals",
        page,
        search,
        status,
        job_commitment,
        qualification,
        type,
        is_supervisor,
        is_lead,
        supervisor_id,
      ],
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 30, // 30 minutes
      // Keeps the last response while a new filter loads, so the supervisor
      // dropdown (fed from it) doesn't empty out on every change.
      placeholderData: keepPreviousData,
      queryFn: () =>
        getProfessionals({
          page,
          search,
          status,
          job_commitment,
          qualification,
          type,
          is_supervisor,
          is_lead,
          supervisor_id,
        }),
    });

  return { data, isLoading, error, isFetching, isSuccess };
}
